import type { BreakthroughAssessment, Candle, StructuralZone } from '../types/market.js';
import { averageTrueRange } from './indicators.js';
import { clamp, percentChange } from './number.js';
import { detectStructuralZones } from './supportResistance.js';

function empty(explanation: string, nextHistoricalBoundary: number | null = null): BreakthroughAssessment {
  return {
    direction: 'neutral',
    state: 'none',
    candlesSurpassed: 0,
    durationMs: 0,
    boundary: null,
    boundaryStrength: 0,
    acceptancePercent: 0,
    penetrationPercent: 0,
    nextHistoricalBoundary,
    explanation,
  };
}

function countTouches(history: Candle[], boundary: number, tolerance: number, side: 'high' | 'low'): number {
  return history.filter((candle) => Math.abs((side === 'high' ? candle.high : candle.low) - boundary) <= tolerance).length;
}

interface BoundaryCandidate {
  direction: 'long' | 'short';
  boundary: number;
  depth: number;
  strength: number;
  touches: number;
  source: 'record-range' | 'structural-zone';
}

function recordRangeCandidate(history: Candle[], current: Candle, atr: number, tolerance: number): BoundaryCandidate[] {
  let bullishDepth = 0;
  let bearishDepth = 0;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const candle = history[index];
    if (!candle) continue;
    if (current.high > candle.high) bullishDepth += 1;
    else break;
  }
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const candle = history[index];
    if (!candle) continue;
    if (current.low < candle.low) bearishDepth += 1;
    else break;
  }

  const candidates: BoundaryCandidate[] = [];
  if (bullishDepth > 0) {
    const boundary = Math.max(...history.slice(-bullishDepth).map((candle) => candle.high));
    const touches = countTouches(history.slice(-bullishDepth), boundary, tolerance, 'high');
    candidates.push({
      direction: 'long',
      boundary,
      depth: bullishDepth,
      touches,
      strength: Math.round(clamp(Math.log2(bullishDepth + 1) * 18 + touches * 8 + Math.max(0, (current.high - boundary) / atr) * 4, 0, 100)),
      source: 'record-range',
    });
  }
  if (bearishDepth > 0) {
    const boundary = Math.min(...history.slice(-bearishDepth).map((candle) => candle.low));
    const touches = countTouches(history.slice(-bearishDepth), boundary, tolerance, 'low');
    candidates.push({
      direction: 'short',
      boundary,
      depth: bearishDepth,
      touches,
      strength: Math.round(clamp(Math.log2(bearishDepth + 1) * 18 + touches * 8 + Math.max(0, (boundary - current.low) / atr) * 4, 0, 100)),
      source: 'record-range',
    });
  }
  return candidates;
}

function structuralZoneCandidates(history: Candle[], current: Candle, tolerance: number): BoundaryCandidate[] {
  const zones = detectStructuralZones(history, current.close, 18);
  const crossedThisCandle = (zone: StructuralZone, direction: 'long' | 'short'): boolean => {
    if (direction === 'long') {
      return current.open <= zone.high + tolerance && current.high > zone.high && current.low <= zone.high + tolerance;
    }
    return current.open >= zone.low - tolerance && current.low < zone.low && current.high >= zone.low - tolerance;
  };

  const candidates: BoundaryCandidate[] = [];
  for (const zone of zones) {
    const wasResistance = zone.source.some((source) => source.includes('swing high') || source.includes('bearish impulse origin'));
    const wasSupport = zone.source.some((source) => source.includes('swing low') || source.includes('bullish impulse origin'));
    if (wasResistance && crossedThisCandle(zone, 'long')) {
      candidates.push({
        direction: 'long',
        boundary: zone.high,
        depth: zone.historyDepthCandles,
        strength: zone.strength,
        touches: zone.touches,
        source: 'structural-zone',
      });
    }
    if (wasSupport && crossedThisCandle(zone, 'short')) {
      candidates.push({
        direction: 'short',
        boundary: zone.low,
        depth: zone.historyDepthCandles,
        strength: zone.strength,
        touches: zone.touches,
        source: 'structural-zone',
      });
    }
  }
  return candidates;
}

function chooseCandidate(candidates: BoundaryCandidate[], current: Candle, atr: number): BoundaryCandidate | undefined {
  return [...candidates].sort((left, right) => {
    const leftPenetration = left.direction === 'long' ? (current.high - left.boundary) / atr : (left.boundary - current.low) / atr;
    const rightPenetration = right.direction === 'long' ? (current.high - right.boundary) / atr : (right.boundary - current.low) / atr;
    const leftScore = left.strength + Math.log2(left.depth + 1) * 8 + clamp(leftPenetration, 0, 3) * 5;
    const rightScore = right.strength + Math.log2(right.depth + 1) * 8 + clamp(rightPenetration, 0, 3) * 5;
    return rightScore - leftScore;
  })[0];
}

export function assessHistoricalBreakthrough(candles: Candle[]): BreakthroughAssessment {
  if (candles.length < 8) return empty('More same-timeframe candle history is required.');

  const current = candles.at(-1)!;
  const history = candles.slice(0, -1).slice(-500);
  const atr = averageTrueRange(history, Math.min(14, history.length - 1)).filter(Number.isFinite).at(-1)
    ?? Math.max(current.high - current.low, current.close * 0.005);
  const tolerance = Math.max(atr * 0.28, current.close * 0.001);
  const candidate = chooseCandidate(
    [...recordRangeCandidate(history, current, atr, tolerance), ...structuralZoneCandidates(history, current, tolerance)],
    current,
    atr,
  );

  if (!candidate) {
    const zones = detectStructuralZones(history, current.close, 12);
    const nearest = zones
      .filter((zone) => Math.abs(zone.distancePercent) <= Math.max(0.8, (atr / current.close) * 100 * 1.4))
      .sort((left, right) => Math.abs(left.distancePercent) - Math.abs(right.distancePercent))[0];
    if (nearest) {
      const boundary = nearest.kind === 'resistance' ? nearest.low : nearest.high;
      return {
        ...empty('Price is approaching an adaptive same-timeframe historical zone; no breakthrough is confirmed yet.', boundary),
        state: 'approaching',
        boundary,
        boundaryStrength: nearest.strength,
        candlesSurpassed: nearest.historyDepthCandles,
        durationMs: current.openTime - (history.at(-nearest.historyDepthCandles)?.openTime ?? current.openTime),
      };
    }
    return empty('The active candle remains inside its loaded same-timeframe historical structure.');
  }

  const { direction, boundary, depth: candlesSurpassed, strength: boundaryStrength, source } = candidate;
  const durationMs = current.openTime - (history.at(-Math.min(candlesSurpassed, history.length))?.openTime ?? current.openTime);
  const penetrationPercent = direction === 'long'
    ? percentChange(current.high, boundary)
    : percentChange(boundary, current.low);
  const nextHistoricalBoundary = direction === 'long'
    ? history.map((candle) => candle.high).filter((price) => price > current.high).sort((a, b) => a - b)[0] ?? null
    : history.map((candle) => candle.low).filter((price) => price < current.low).sort((a, b) => b - a)[0] ?? null;

  const bodyTop = Math.max(current.open, current.close);
  const bodyBottom = Math.min(current.open, current.close);
  const range = Math.max(current.high - current.low, Number.EPSILON);
  const bodyBeyond = direction === 'long' ? current.close > boundary : current.close < boundary;
  const wickBeyond = direction === 'long' ? current.high > boundary : current.low < boundary;
  const returnedInside = direction === 'long' ? current.close < boundary : current.close > boundary;
  const retested = direction === 'long'
    ? current.open >= boundary && current.low <= boundary && current.close > boundary
    : current.open <= boundary && current.high >= boundary && current.close < boundary;
  const bodyDistanceBeyond = direction === 'long'
    ? Math.max(0, bodyTop - Math.max(boundary, bodyBottom))
    : Math.max(0, Math.min(boundary, bodyTop) - bodyBottom);
  const acceptancePercent = clamp((bodyDistanceBeyond / range) * 100, 0, 100);
  const closedBeyond = current.closed && bodyBeyond;
  const state: BreakthroughAssessment['state'] = returnedInside && wickBeyond
    ? 'failed'
    : retested
      ? 'retest'
      : closedBeyond
        ? 'closed-beyond'
        : bodyBeyond
          ? acceptancePercent >= 20
            ? 'holding-beyond'
            : 'body-penetration'
          : wickBeyond
            ? 'wick-probe'
            : 'approaching';

  const directionLabel = direction === 'long' ? 'above' : 'below';
  const sourceLabel = source === 'structural-zone' ? 'an adaptive reaction/impulse zone' : 'the uninterrupted recent range';
  let explanation: string;
  if (state === 'failed') {
    explanation = `Price probed ${directionLabel} ${sourceLabel} spanning roughly ${candlesSurpassed} same-timeframe candles, then returned inside. This is a possible failed breakthrough, not automatic reversal proof.`;
  } else if (state === 'retest') {
    explanation = `Price crossed ${sourceLabel} and is retesting it while still holding beyond it. Continued acceptance, remaining candle time, and market participation matter more than the touch alone.`;
  } else {
    explanation = `The active candle has moved ${directionLabel} ${sourceLabel} discovered across roughly ${candlesSurpassed} candles (${Math.round(durationMs / 3_600_000)} hours in loaded history). The event is ${state.replace('-', ' ')} with ${acceptancePercent.toFixed(0)}% body acceptance; it remains one evidence point, not a final prediction.`;
  }

  return {
    direction,
    state,
    candlesSurpassed,
    durationMs,
    boundary,
    boundaryStrength,
    acceptancePercent,
    penetrationPercent,
    nextHistoricalBoundary,
    explanation,
  };
}
